import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface ChildEnrollment {
  enrollmentId: string;
  classId: string;
  className: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  room: string | null;
  teacher: string | null;
  attendance: {
    present: number;
    total: number;
    percentage: number;
  };
}

interface ChildData {
  memberId: string;
  name: string;
  enrollments: ChildEnrollment[];
}

export function SchoolPage() {
  useAuth();

  const { data: children, isLoading } = useQuery({
    queryKey: ['my-children-school'],
    queryFn: async () => {
      const response = await api.get<ChildData[]>('/school/my-children');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Religious School</h1>
          <p className="mt-2 text-gray-600">
            View your children's classes and attendance
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : !children || children.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No school enrollments</h3>
            <p className="text-gray-500">
              Contact the synagogue office to enroll in religious school classes.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {children.map((child) => (
              <ChildSection key={child.memberId} child={child} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChildSection({ child }: { child: ChildData }) {
  const [expandedClass, setExpandedClass] = useState<string | null>(null);

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{child.name}</h2>

      <div className="space-y-4">
        {child.enrollments.map((enrollment) => (
          <div
            key={enrollment.enrollmentId}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{enrollment.className}</h3>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <p>{DAY_NAMES[enrollment.dayOfWeek]}s, {enrollment.startTime} - {enrollment.endTime}</p>
                    {enrollment.room && <p>Room: {enrollment.room}</p>}
                    {enrollment.teacher && <p>Teacher: {enrollment.teacher}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium ${
                    enrollment.attendance.percentage >= 75
                      ? 'bg-green-50 text-green-700'
                      : enrollment.attendance.percentage >= 50
                      ? 'bg-yellow-50 text-yellow-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {enrollment.attendance.total > 0
                      ? `Present: ${enrollment.attendance.present}/${enrollment.attendance.total} (${enrollment.attendance.percentage}%)`
                      : 'No attendance yet'
                    }
                  </div>
                </div>
              </div>
            </div>

            {enrollment.attendance.total > 0 && (
              <div className="border-t border-gray-100 px-6 py-2">
                <button
                  onClick={() =>
                    setExpandedClass(expandedClass === enrollment.classId ? null : enrollment.classId)
                  }
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {expandedClass === enrollment.classId ? 'Hide Details' : 'View Details'}
                </button>
              </div>
            )}

            {expandedClass === enrollment.classId && (
              <AttendanceDetail classId={enrollment.classId} memberId={child.memberId} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AttendanceDetail({ classId, memberId }: { classId: string; memberId: string }) {
  const { data: attendance, isLoading } = useQuery({
    queryKey: ['my-child-attendance', classId, memberId],
    queryFn: async () => {
      // Use the school attendance endpoint (parent route)
      // This re-fetches from the my-children data so we just show a summary
      const response = await api.get<ChildData[]>('/school/my-children');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  if (isLoading) {
    return (
      <div className="px-6 py-4 border-t border-gray-100">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  // Find the relevant enrollment data
  const child = attendance?.find((c) => c.memberId === memberId);
  const enrollment = child?.enrollments.find((e) => e.classId === classId);

  if (!enrollment) return null;

  return (
    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
      <div className="grid grid-cols-4 gap-4 text-center text-sm">
        <div>
          <p className="font-medium text-green-600">{enrollment.attendance.present}</p>
          <p className="text-gray-500">Present</p>
        </div>
        <div>
          <p className="font-medium text-gray-900">{enrollment.attendance.total}</p>
          <p className="text-gray-500">Total Classes</p>
        </div>
        <div>
          <p className="font-medium text-gray-900">{enrollment.attendance.total - enrollment.attendance.present}</p>
          <p className="text-gray-500">Missed</p>
        </div>
        <div>
          <p className={`font-medium ${
            enrollment.attendance.percentage >= 75
              ? 'text-green-600'
              : enrollment.attendance.percentage >= 50
              ? 'text-yellow-600'
              : 'text-red-600'
          }`}>{enrollment.attendance.percentage}%</p>
          <p className="text-gray-500">Rate</p>
        </div>
      </div>
    </div>
  );
}
